import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  useGetReleveCorrectionByIdQuery,
  useValiderLigneCorrectionMutation,
  useValiderToutCorrectionMutation,
  useGetCopiesManquantesQuery,
  LigneReleveCorrectionOCR,
} from '../../services/ocrApi';
import {
  Eye,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Loader,
  ChevronLeft,
  ChevronRight,
  Check,
  RotateCcw,
  ZoomIn,
  ZoomOut,
  AlertCircle,
  CheckSquare,
  PenTool,
  GitCompare,
} from 'lucide-react';

const VerificationOCRCorrection: React.FC = () => {
  const { releveId } = useParams<{ releveId: string }>();
  const navigate = useNavigate();

  // State
  const [selectedLigneIndex, setSelectedLigneIndex] = useState(0);
  const [editedValues, setEditedValues] = useState<Record<number, {
    note_finale?: number | null;
    absent?: boolean;
    commentaire?: string;
  }>>({});
  const [zoomLevel, setZoomLevel] = useState(100);
  const [showOnlyToVerify, setShowOnlyToVerify] = useState(true);
  const [showCopiesManquantes, setShowCopiesManquantes] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Queries et Mutations
  const { data: releve, isLoading, refetch } = useGetReleveCorrectionByIdQuery(
    parseInt(releveId || '0'),
    { skip: !releveId }
  );
  const { data: copiesManquantes } = useGetCopiesManquantesQuery(
    parseInt(releveId || '0'),
    { skip: !releveId }
  );
  const [validerLigne, { isLoading: validerLoading }] = useValiderLigneCorrectionMutation();
  const [validerTout, { isLoading: validerToutLoading }] = useValiderToutCorrectionMutation();

  // Filtrer les lignes
  const lignes = releve?.lignes_ocr || [];
  const lignesFiltrees = showOnlyToVerify
    ? lignes.filter((l) => l.necessite_verification && !l.verifie)
    : lignes;
  const ligneCourante = lignesFiltrees[selectedLigneIndex];

  // Initialiser les valeurs éditées
  useEffect(() => {
    if (ligneCourante && !editedValues[ligneCourante.id]) {
      setEditedValues((prev) => ({
        ...prev,
        [ligneCourante.id]: {
          note_finale: ligneCourante.note_finale,
          absent: ligneCourante.absent_valide,
          commentaire: ligneCourante.commentaire || '',
        },
      }));
    }
  }, [ligneCourante]);

  const currentValues = ligneCourante ? editedValues[ligneCourante.id] : null;

  // Navigation
  const handlePrevious = () => {
    if (selectedLigneIndex > 0) {
      setSelectedLigneIndex(selectedLigneIndex - 1);
    }
  };

  const handleNext = () => {
    if (selectedLigneIndex < lignesFiltrees.length - 1) {
      setSelectedLigneIndex(selectedLigneIndex + 1);
    }
  };

  // Mise à jour des valeurs
  const updateValue = (field: string, value: any) => {
    if (!ligneCourante) return;
    setEditedValues((prev) => ({
      ...prev,
      [ligneCourante.id]: {
        ...prev[ligneCourante.id],
        [field]: value,
      },
    }));
  };

  // Valider la ligne courante
  const handleValiderLigne = async () => {
    if (!ligneCourante || !currentValues || !releveId) return;

    try {
      await validerLigne({
        releveId: parseInt(releveId),
        data: {
          ligne_id: ligneCourante.id,
          note_finale: currentValues.absent ? undefined : currentValues.note_finale ?? undefined,
          absent: currentValues.absent,
          commentaire: currentValues.commentaire,
        },
      }).unwrap();

      setMessage({ type: 'success', text: 'Ligne validée avec succès' });

      // Passer à la ligne suivante si possible
      if (selectedLigneIndex < lignesFiltrees.length - 1) {
        setTimeout(() => {
          setSelectedLigneIndex(selectedLigneIndex + 1);
          setMessage(null);
        }, 500);
      }

      refetch();
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error?.data?.error || 'Erreur lors de la validation',
      });
    }
  };

  // Valider tout le relevé
  const handleValiderTout = async () => {
    if (!releveId) return;

    try {
      await validerTout(parseInt(releveId)).unwrap();
      setMessage({ type: 'success', text: 'Relevé validé et notes enregistrées avec succès' });
      setTimeout(() => navigate('/ocr/correction'), 1500);
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error?.data?.error || 'Erreur lors de la validation du relevé',
      });
    }
  };

  // Reset aux valeurs détectées
  const handleReset = () => {
    if (!ligneCourante) return;
    setEditedValues((prev) => ({
      ...prev,
      [ligneCourante.id]: {
        note_finale: ligneCourante.note_finale,
        absent: ligneCourante.absent_detecte,
        commentaire: '',
      },
    }));
  };

  // Choisir la source de la note
  const handleSelectSource = (source: 'cases' | 'manuscrit') => {
    if (!ligneCourante) return;
    const note = source === 'cases'
      ? ligneCourante.note_cases_detectee
      : ligneCourante.note_manuscrite_detectee;
    updateValue('note_finale', note);
  };

  // Indicateurs visuels
  const getConfianceColor = (confiance: number) => {
    if (confiance >= 0.9) return 'text-green-600 bg-green-100 dark:bg-green-900/30 dark:text-green-400';
    if (confiance >= 0.7) return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/30 dark:text-yellow-400';
    return 'text-red-600 bg-red-100 dark:bg-red-900/30 dark:text-red-400';
  };

  const getSourceIcon = (source: string) => {
    switch (source) {
      case 'cases':
        return <CheckSquare size={16} className="text-blue-600" />;
      case 'manuscrit':
        return <PenTool size={16} className="text-purple-600" />;
      case 'croise':
        return <GitCompare size={16} className="text-green-600" />;
      default:
        return <AlertCircle size={16} className="text-orange-600" />;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader className="animate-spin text-blue-600" size={48} />
      </div>
    );
  }

  if (!releve) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-300 p-4 rounded-lg">
          Relevé non trouvé
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gray-100 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b dark:border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/ocr/correction')}
              className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
            >
              <ChevronLeft size={24} />
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100 flex items-center">
                <Eye className="mr-2" size={24} />
                Vérification OCR - {releve.code_releve}
              </h1>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {releve.matiere_code} - {releve.matiere_nom} | {releve.niveau} {releve.option} |{' '}
                {releve.type_examen === 'rattrapage' ? 'Rattrapage' : 'Session ordinaire'}
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-4">
            {/* Copies manquantes */}
            {copiesManquantes && copiesManquantes.total > 0 && (
              <button
                onClick={() => setShowCopiesManquantes(true)}
                className="flex items-center px-3 py-2 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded hover:bg-red-200 dark:hover:bg-red-800"
              >
                <AlertTriangle size={18} className="mr-1" />
                {copiesManquantes.total} copies manquantes
              </button>
            )}

            {/* Toggle filter */}
            <label className="flex items-center space-x-2 text-sm">
              <input
                type="checkbox"
                checked={showOnlyToVerify}
                onChange={(e) => {
                  setShowOnlyToVerify(e.target.checked);
                  setSelectedLigneIndex(0);
                }}
                className="rounded border-gray-300 dark:border-gray-600"
              />
              <span className="dark:text-gray-300">Uniquement à vérifier</span>
            </label>

            {/* Stats */}
            <div className="text-sm text-gray-600 dark:text-gray-400">
              {lignesFiltrees.length > 0 ? (
                <span>
                  {selectedLigneIndex + 1} / {lignesFiltrees.length}
                  {showOnlyToVerify && ` (${lignes.length} total)`}
                </span>
              ) : (
                <span className="text-green-600 dark:text-green-400">Toutes les lignes vérifiées</span>
              )}
            </div>

            {/* Valider tout */}
            <button
              onClick={handleValiderTout}
              disabled={validerToutLoading || releve.lignes_a_verifier > 0}
              className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {validerToutLoading ? (
                <Loader className="animate-spin mr-2" size={18} />
              ) : (
                <CheckCircle2 className="mr-2" size={18} />
              )}
              Valider tout ({releve.lignes_a_verifier} restants)
            </button>
          </div>
        </div>

        {/* Message */}
        {message && (
          <div
            className={`mt-4 p-3 rounded-lg flex items-center ${
              message.type === 'success'
                ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-300'
                : 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-300'
            }`}
          >
            {message.type === 'success' ? (
              <CheckCircle2 className="mr-2" size={18} />
            ) : (
              <XCircle className="mr-2" size={18} />
            )}
            {message.text}
          </div>
        )}
      </div>

      {/* Main content */}
      {lignesFiltrees.length === 0 ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <CheckCircle2 className="mx-auto text-green-500" size={64} />
            <h2 className="mt-4 text-xl font-semibold text-gray-900 dark:text-gray-100">
              Toutes les lignes ont été vérifiées
            </h2>
            <p className="mt-2 text-gray-600 dark:text-gray-400">
              Vous pouvez maintenant valider le relevé complet.
            </p>
          </div>
        </div>
      ) : (
        <div className="flex-1 flex overflow-hidden">
          {/* Panel Image (gauche) */}
          <div className="w-1/2 bg-gray-800 dark:bg-gray-950 p-4 overflow-auto">
            <div className="flex items-center justify-between mb-4">
              <span className="text-white text-sm">
                Image de la ligne {ligneCourante?.numero_ligne} (Page {ligneCourante?.numero_page})
              </span>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setZoomLevel(Math.max(50, zoomLevel - 25))}
                  className="p-1 text-white hover:bg-gray-700 rounded"
                >
                  <ZoomOut size={20} />
                </button>
                <span className="text-white text-sm">{zoomLevel}%</span>
                <button
                  onClick={() => setZoomLevel(Math.min(200, zoomLevel + 25))}
                  className="p-1 text-white hover:bg-gray-700 rounded"
                >
                  <ZoomIn size={20} />
                </button>
              </div>
            </div>

            {ligneCourante?.image_base64 ? (
              <div className="flex justify-center">
                <img
                  src={`data:image/png;base64,${ligneCourante.image_base64}`}
                  alt={`Ligne ${ligneCourante.numero_ligne}`}
                  style={{ width: `${zoomLevel}%` }}
                  className="border border-gray-600 rounded"
                />
              </div>
            ) : (
              <div className="flex items-center justify-center h-64 text-gray-400">
                Aucune image disponible
              </div>
            )}
          </div>

          {/* Panel Données (droite) */}
          <div className="w-1/2 bg-white dark:bg-gray-800 p-6 overflow-auto">
            {ligneCourante && currentValues && (
              <div className="space-y-6">
                {/* Informations copie */}
                <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">Copie d'examen</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="text-sm text-gray-600 dark:text-gray-400">Code copie détecté:</span>
                      <div className="font-mono font-semibold text-lg">
                        {ligneCourante.code_copie_detecte || 'N/A'}
                      </div>
                      <span
                        className={`inline-block mt-1 px-2 py-0.5 rounded text-xs ${getConfianceColor(
                          ligneCourante.confiance_code
                        )}`}
                      >
                        {(ligneCourante.confiance_code * 100).toFixed(0)}% confiance
                      </span>
                    </div>
                    {ligneCourante.copie_examen && (
                      <div>
                        <span className="text-sm text-gray-600 dark:text-gray-400">Étudiant associé:</span>
                        <div className="font-semibold dark:text-gray-100">{ligneCourante.etudiant_nom}</div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {ligneCourante.etudiant_matricule} ({ligneCourante.copie_code})
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Comparaison Cases vs Manuscrit */}
                <div className="grid grid-cols-2 gap-4">
                  {/* Note cases cochées */}
                  <div
                    className={`rounded-lg p-4 border-2 cursor-pointer transition-all ${
                      ligneCourante.source_note === 'cases'
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 hover:border-blue-300'
                    }`}
                    onClick={() => handleSelectSource('cases')}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center">
                        <CheckSquare className="mr-2 text-blue-600" size={20} />
                        <span className="font-semibold text-gray-900 dark:text-gray-100">Cases cochées</span>
                      </div>
                      <span
                        className={`px-2 py-0.5 rounded text-xs ${getConfianceColor(
                          ligneCourante.confiance_cases
                        )}`}
                      >
                        {(ligneCourante.confiance_cases * 100).toFixed(0)}%
                      </span>
                    </div>
                    <div className="text-2xl font-bold text-blue-700 dark:text-blue-300">
                      {ligneCourante.note_cases_detectee !== null
                        ? `${ligneCourante.note_cases_detectee}/20`
                        : 'N/A'}
                    </div>
                    {ligneCourante.cases_detectees && (
                      <div className="mt-2 text-xs text-gray-600 dark:text-gray-400">
                        Entier: {ligneCourante.cases_detectees.entier ?? '-'} |
                        Décimal: {ligneCourante.cases_detectees.decimal ?? '-'}
                      </div>
                    )}
                  </div>

                  {/* Note manuscrite */}
                  <div
                    className={`rounded-lg p-4 border-2 cursor-pointer transition-all ${
                      ligneCourante.source_note === 'manuscrit'
                        ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                        : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 hover:border-purple-300'
                    }`}
                    onClick={() => handleSelectSource('manuscrit')}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center">
                        <PenTool className="mr-2 text-purple-600" size={20} />
                        <span className="font-semibold text-gray-900 dark:text-gray-100">Note manuscrite</span>
                      </div>
                      <span
                        className={`px-2 py-0.5 rounded text-xs ${getConfianceColor(
                          ligneCourante.confiance_manuscrit
                        )}`}
                      >
                        {(ligneCourante.confiance_manuscrit * 100).toFixed(0)}%
                      </span>
                    </div>
                    <div className="text-2xl font-bold text-purple-700 dark:text-purple-300">
                      {ligneCourante.note_manuscrite_detectee !== null
                        ? `${ligneCourante.note_manuscrite_detectee}/20`
                        : 'N/A'}
                    </div>
                  </div>
                </div>

                {/* Source et cohérence */}
                <div className="flex items-center justify-between bg-gray-100 dark:bg-gray-700 rounded-lg p-3">
                  <div className="flex items-center space-x-2">
                    {getSourceIcon(ligneCourante.source_note)}
                    <span className="text-sm">
                      Source: <strong>{ligneCourante.source_note}</strong>
                    </span>
                  </div>
                  <span
                    className={`px-2 py-1 rounded text-sm font-medium ${getConfianceColor(
                      ligneCourante.confiance_finale
                    )}`}
                  >
                    Confiance finale: {(ligneCourante.confiance_finale * 100).toFixed(0)}%
                  </span>
                </div>

                {/* Raison de vérification */}
                {ligneCourante.raison_verification && (
                  <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                    <div className="flex items-center text-yellow-800 dark:text-yellow-300">
                      <AlertTriangle className="mr-2" size={20} />
                      <span className="font-medium">Raison de la vérification:</span>
                    </div>
                    <p className="mt-1 text-sm text-yellow-700 dark:text-yellow-300">{ligneCourante.raison_verification}</p>
                  </div>
                )}

                {/* Erreur éventuelle */}
                {ligneCourante.erreur && (
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                    <div className="flex items-center text-red-800 dark:text-red-300">
                      <XCircle className="mr-2" size={20} />
                      <span className="font-medium">Erreur détectée:</span>
                    </div>
                    <p className="mt-1 text-sm text-red-700 dark:text-red-300">{ligneCourante.erreur}</p>
                  </div>
                )}

                {/* Note finale */}
                <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 border border-green-200 dark:border-green-800">
                  <h4 className="font-semibold text-green-900 dark:text-green-100 mb-3">Note finale validée</h4>

                  <div className="space-y-3">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={currentValues.absent || false}
                        onChange={(e) => updateValue('absent', e.target.checked)}
                        className="rounded border-gray-300 dark:border-gray-600"
                      />
                      <span className="text-sm dark:text-gray-300">Absent</span>
                    </label>

                    {!currentValues.absent && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Note /20
                        </label>
                        <input
                          type="number"
                          min="0"
                          max="20"
                          step="0.25"
                          value={currentValues.note_finale ?? ''}
                          onChange={(e) =>
                            updateValue(
                              'note_finale',
                              e.target.value ? parseFloat(e.target.value) : null
                            )
                          }
                          className="w-full px-4 py-3 text-xl font-bold border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-gray-100"
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Commentaire */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Commentaire (optionnel)
                  </label>
                  <textarea
                    value={currentValues.commentaire || ''}
                    onChange={(e) => updateValue('commentaire', e.target.value)}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
                    placeholder="Ajouter un commentaire si nécessaire..."
                  />
                </div>

                {/* Actions */}
                <div className="flex items-center justify-between pt-4 border-t dark:border-gray-700">
                  <div className="flex space-x-2">
                    <button
                      onClick={handlePrevious}
                      disabled={selectedLigneIndex === 0}
                      className="flex items-center px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft size={18} className="mr-1" />
                      Précédent
                    </button>
                    <button
                      onClick={handleNext}
                      disabled={selectedLigneIndex === lignesFiltrees.length - 1}
                      className="flex items-center px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Suivant
                      <ChevronRight size={18} className="ml-1" />
                    </button>
                  </div>

                  <div className="flex space-x-2">
                    <button
                      onClick={handleReset}
                      className="flex items-center px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600"
                    >
                      <RotateCcw size={18} className="mr-1" />
                      Réinitialiser
                    </button>
                    <button
                      onClick={handleValiderLigne}
                      disabled={validerLoading}
                      className="flex items-center px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                    >
                      {validerLoading ? (
                        <Loader className="animate-spin mr-2" size={18} />
                      ) : (
                        <Check size={18} className="mr-2" />
                      )}
                      Valider cette ligne
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Liste des lignes (bas) */}
      <div className="bg-white dark:bg-gray-800 border-t dark:border-gray-700 shadow-inner">
        <div className="px-6 py-3 overflow-x-auto">
          <div className="flex space-x-2">
            {lignesFiltrees.map((ligne, index) => (
              <button
                key={ligne.id}
                onClick={() => setSelectedLigneIndex(index)}
                className={`flex-shrink-0 px-3 py-2 rounded border text-sm ${
                  index === selectedLigneIndex
                    ? 'bg-blue-600 text-white border-blue-600'
                    : ligne.verifie
                    ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 border-green-300 dark:border-green-700'
                    : ligne.necessite_verification
                    ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 border-yellow-300 dark:border-yellow-700'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600'
                }`}
              >
                <div className="font-mono">{ligne.code_copie_detecte || `L${ligne.numero_ligne}`}</div>
                <div className="text-xs opacity-75 flex items-center">
                  {getSourceIcon(ligne.source_note)}
                  <span className="ml-1">{ligne.note_finale ?? '-'}/20</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Modal Copies Manquantes */}
      {showCopiesManquantes && copiesManquantes && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-96 overflow-hidden">
            <div className="p-6 border-b dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center">
                <AlertTriangle className="mr-2 text-red-500" size={24} />
                Copies manquantes ({copiesManquantes.total})
              </h3>
            </div>
            <div className="p-6 overflow-y-auto max-h-64">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Les copies suivantes n'ont pas été détectées dans le scan:
              </p>
              <div className="space-y-2">
                {copiesManquantes.copies_manquantes.map((copie, index) => (
                  <div key={index} className="p-3 bg-red-50 dark:bg-red-900/20 rounded border border-red-200 dark:border-red-800">
                    <div className="font-mono font-semibold text-red-800 dark:text-red-300">{copie.code_copie}</div>
                    {copie.etudiant_nom && (
                      <div className="text-sm text-red-600 dark:text-red-400">
                        {copie.etudiant_nom} ({copie.etudiant_matricule})
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div className="p-6 border-t dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
              <button
                onClick={() => setShowCopiesManquantes(false)}
                className="w-full px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-600"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VerificationOCRCorrection;
